import i18n from '@libs/i18n'

import {
  CustomPlanName,
  IPaymentPlan, // IPlan,
  PaymentPlanStatus,
  PaymentPlanType,
  PlanPeriod,
  StandardPlanName,
} from '@utils/types'

const t = i18n.t.bind(i18n)

export const FEATURES_LIST = [
  {
    name: 'Vendor Profile Page',
    [StandardPlanName.BETA]: true,
    [StandardPlanName.STARTER]: true,
    [StandardPlanName.PLUS]: true,
    [StandardPlanName.MAX]: true,
    [CustomPlanName.ENTERPRISE]: true,
  },
  {
    name: 'Solution Landing Pages',
    [StandardPlanName.BETA]: '(Up to ---)',
    [StandardPlanName.STARTER]: '(Up to ---)',
    [StandardPlanName.PLUS]: '(Up to ---)',
    [StandardPlanName.MAX]: '(Up to ---)',
    [CustomPlanName.ENTERPRISE]: '(Custom)',
  },
  {
    name: 'Solution Builder',
    [StandardPlanName.BETA]: true,
    [StandardPlanName.STARTER]: true,
    [StandardPlanName.PLUS]: true,
    [StandardPlanName.MAX]: true,
    [CustomPlanName.ENTERPRISE]: true,
  },
  {
    name: 'Setup of Lead Criteria',
    [StandardPlanName.BETA]: true,
    [StandardPlanName.STARTER]: true,
    [StandardPlanName.PLUS]: true,
    [StandardPlanName.MAX]: true,
    [CustomPlanName.ENTERPRISE]: true,
  },
  {
    name: 'Schedule Meetings with Leads',
    [StandardPlanName.BETA]: false,
    [StandardPlanName.STARTER]: true,
    [StandardPlanName.PLUS]: true,
    [StandardPlanName.MAX]: true,
    [CustomPlanName.ENTERPRISE]: true,
  },
  {
    name: 'Realtime Lead Chat',
    [StandardPlanName.BETA]: true,
    [StandardPlanName.STARTER]: true,
    [StandardPlanName.PLUS]: true,
    [StandardPlanName.MAX]: true,
    [CustomPlanName.ENTERPRISE]: true,
  },
  {
    name: 'Calendar Management',
    [StandardPlanName.BETA]: true,
    [StandardPlanName.STARTER]: true,
    [StandardPlanName.PLUS]: true,
    [StandardPlanName.MAX]: true,
    [CustomPlanName.ENTERPRISE]: true,
  },
  {
    name: 'Lead Management',
    [StandardPlanName.BETA]: true,
    [StandardPlanName.STARTER]: true,
    [StandardPlanName.PLUS]: true,
    [StandardPlanName.MAX]: true,
    [CustomPlanName.ENTERPRISE]: true,
  },
  {
    name: 'Lead Lifecycle Tracking',
    [StandardPlanName.BETA]: true,
    [StandardPlanName.STARTER]: true,
    [StandardPlanName.PLUS]: true,
    [StandardPlanName.MAX]: true,
    [CustomPlanName.ENTERPRISE]: true,
  },
  {
    name: 'Success Coach for 30 Days',
    [StandardPlanName.BETA]: true,
    [StandardPlanName.STARTER]: true,
    [StandardPlanName.PLUS]: true,
    [StandardPlanName.MAX]: true,
    [CustomPlanName.ENTERPRISE]: true,
  },
  {
    name: 'Search Analytics',
    [StandardPlanName.BETA]: false,
    [StandardPlanName.STARTER]: true,
    [StandardPlanName.PLUS]: true,
    [StandardPlanName.MAX]: true,
    [CustomPlanName.ENTERPRISE]: true,
  },
  {
    name: 'Market Trends',
    [StandardPlanName.BETA]: false,
    [StandardPlanName.STARTER]: true,
    [StandardPlanName.PLUS]: true,
    [StandardPlanName.MAX]: true,
    [CustomPlanName.ENTERPRISE]: true,
  },
  {
    name: 'Solution Trends',
    [StandardPlanName.BETA]: false,
    [StandardPlanName.STARTER]: true,
    [StandardPlanName.PLUS]: true,
    [StandardPlanName.MAX]: true,
    [CustomPlanName.ENTERPRISE]: true,
  },
  {
    name: 'ACH/Purchase Order Payments',
    [StandardPlanName.BETA]: false,
    [StandardPlanName.STARTER]: false,
    [StandardPlanName.PLUS]: false,
    [StandardPlanName.MAX]: false,
    [CustomPlanName.ENTERPRISE]: true,
  },
  {
    name: 'Support Hours Included',
    [StandardPlanName.BETA]: false,
    [StandardPlanName.STARTER]: false,
    [StandardPlanName.PLUS]: '- 1hr/month',
    [StandardPlanName.MAX]: '- 2hrs/month',
    [CustomPlanName.ENTERPRISE]: '(Custom)',
  },
  {
    name: 'Additional Support Hours',
    [StandardPlanName.BETA]: '- $195/hour',
    [StandardPlanName.STARTER]: '- $195/hour',
    [StandardPlanName.PLUS]: '- $195/hour',
    [StandardPlanName.MAX]: '- $195/hour',
    [CustomPlanName.ENTERPRISE]: '- $195/hour',
  },
]

export const PLAN_STATUS_ITEMS = [
  {
    value: PaymentPlanStatus.AVAILABLE,
    label: t('Available'),
    color: '#AFF9DA',
  },
  {
    value: PaymentPlanStatus.COMING_SOON,
    label: t('Coming Soon'),
    color: '#C1E3F4',
  },
  {
    value: PaymentPlanStatus.HIDDEN,
    label: t('Hidden'),
    color: '#ACBFEB',
  },
]

export const PLAN_TYPE_ITEMS = [
  {
    value: PaymentPlanType.STANDARD,
    label: t('Standard'),
  },
  {
    value: PaymentPlanType.SUPPORT_HOURS,
    label: t('Support Hours'),
  },
  {
    value: PaymentPlanType.ENTERPRISE,
    label: t('Enterprise'),
  },
]

export const PLAN_STATUS_SELECT_ITEMS = PLAN_STATUS_ITEMS.map((item) => {
  return {
    label: item.label,
    value: item.value,
  }
})

const getSupportHoursPlanFromStandardPlan = ({
  standardPlan,
}: {
  standardPlan?: IPaymentPlan
}) => {
  if (!standardPlan) {
    return undefined
  }

  const supportHoursPlan = standardPlan.childrenPlans?.find(
    (plan) => plan.type === PaymentPlanType.SUPPORT_HOURS
  )

  return supportHoursPlan
}

const getPlanPeriodLabel = ({ planPeriod }: { planPeriod?: PlanPeriod }) => {
  if (!planPeriod) {
    return ''
  }

  switch (planPeriod) {
    case PlanPeriod.MONTHLY:
      return t('Monthly')
    case PlanPeriod.BIMONTHLY:
      return t('Bimonthly')
    case PlanPeriod.QUARTERLY:
      return t('Quarterly')
    case PlanPeriod.HALF_YEARLY:
      return t('Half-Yearly')
    case PlanPeriod.YEARLY:
      return t('Yearly')
    case PlanPeriod.BIENNIALLY:
      return t('Biennially')
    default:
      return ''
  }
}

export default {
  FEATURES_LIST,
  PLAN_STATUS_ITEMS,
  PLAN_STATUS_SELECT_ITEMS,
  PLAN_TYPE_ITEMS,
  getSupportHoursPlanFromStandardPlan,
  getPlanPeriodLabel,
}
